import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import type { BookingListInvite, BookingListOffer } from '../../../shared/api.js';
import { api } from '../lib/api';
import { formatShortDateTime } from '../lib/format';

function CopyLink({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    void navigator.clipboard
      ?.writeText(url)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => undefined);
  };
  return (
    <button type="button" className="btn btn--small btn--ghost" onClick={copy}>
      {copied ? 'Скопировано' : 'Копировать ссылку'}
    </button>
  );
}

function InviteRow({ invite, canWrite }: { invite: BookingListInvite; canWrite: boolean }) {
  const queryClient = useQueryClient();
  const retry = useMutation({
    mutationFn: () => api.retryBookingEvent(invite.key),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['bookings'] }),
  });
  let status;
  if (!invite.booked) status = <span className="pill pill--muted">ещё не выбрал(а)</span>;
  else if (!invite.eventCreated)
    status = <span className="pill pill--error">записан(а), не все события созданы</span>;
  else status = <span className="pill pill--ok">записан(а)</span>;
  return (
    <li className="page-row">
      <div className="page-row__main">
        <span className="page-row__title">
          {invite.name} {status}
        </span>
        {invite.slots.map((slot) => (
          <span key={slot} className="small">
            {slot}
          </span>
        ))}
        {invite.eventError && <span className="text-error small">{invite.eventError}</span>}
        {retry.data?.error && <span className="text-error small">{retry.data.error}</span>}
        {retry.isError && <span className="text-error small">{retry.error.message}</span>}
      </div>
      <div className="actions-row">
        {invite.booked && !invite.eventCreated && canWrite && (
          <button
            type="button"
            className="btn btn--small"
            disabled={retry.isPending}
            onClick={() => retry.mutate()}
          >
            {retry.isPending ? 'Создаю…' : 'Создать событие'}
          </button>
        )}
        <CopyLink url={invite.url} />
      </div>
    </li>
  );
}

function OfferCard({ offer, canWrite }: { offer: BookingListOffer; canWrite: boolean }) {
  const queryClient = useQueryClient();
  const close = useMutation({
    mutationFn: () => api.closeBooking(offer.id),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['bookings'] }),
  });
  const byDay = new Map<string, BookingListOffer['slots']>();
  for (const slot of offer.slots) byDay.set(slot.day, [...(byDay.get(slot.day) ?? []), slot]);
  const taken = offer.slots.filter((s) => s.bookedBy).length;

  return (
    <article className="card booking">
      <header className="card__header">
        <div>
          <h3>{offer.title}</h3>
          <span className="muted small">
            {offer.durationMin} мин
            {offer.maxSlots > 1 && ` · до ${offer.maxSlots} слотов на человека`}
            {offer.zoom && ' · Zoom'} · создан {formatShortDateTime(offer.createdAt)} · занято{' '}
            {taken} из {offer.slots.length}
          </span>
        </div>
        {offer.closed ? (
          <span className="pill pill--muted">закрыт</span>
        ) : (
          canWrite && (
            <button
              type="button"
              className="btn btn--small btn--ghost"
              disabled={close.isPending}
              onClick={() => {
                if (
                  window.confirm(`Закрыть «${offer.title}»? Ссылки перестанут принимать запись.`)
                ) {
                  close.mutate();
                }
              }}
            >
              Закрыть набор
            </button>
          )
        )}
      </header>
      {close.isError && <p className="text-error small">{close.error.message}</p>}

      <h4>Люди</h4>
      <ul className="page-list">
        {offer.invites.map((invite) => (
          <InviteRow key={invite.key} invite={invite} canWrite={canWrite} />
        ))}
      </ul>

      <h4>Слоты</h4>
      {[...byDay].map(([day, slots]) => (
        <div key={day} className="booking__day">
          <span className="booking__date">{day}</span>
          <div className="booking__slots">
            {slots.map((s) => (
              <span
                key={s.id ?? s.start}
                className={`slot-chip${s.bookedBy ? ' slot-chip--taken' : ''}`}
                title={s.bookedBy ? `Занят: ${s.bookedBy}` : 'Свободен'}
              >
                {s.time}
                {s.bookedBy && <b> · {s.bookedBy}</b>}
              </span>
            ))}
          </div>
        </div>
      ))}
      <p className="muted small">
        Время — {offer.tz}. Свободные слоты — контуром, занятые — закрашены.
      </p>
    </article>
  );
}

/** Booking links the agent made: who picked what, which slots are left. */
export function Bookings({ canWrite }: { canWrite: boolean }) {
  const [all, setAll] = useState(false);
  const query = useQuery({
    queryKey: ['bookings', all],
    queryFn: () => api.bookings(all),
    refetchInterval: 30_000,
  });
  const offers = query.data?.offers ?? [];
  return (
    <div className="page">
      <div className="page__header">
        <h2>Записи</h2>
        <label className="small">
          <input
            type="checkbox"
            className="inline-check"
            checked={all}
            onChange={(e) => setAll(e.target.checked)}
          />{' '}
          показывать закрытые
        </label>
      </div>
      {query.isPending && <p className="muted">Загрузка…</p>}
      {query.isError && <p className="text-error">{query.error.message}</p>}
      {query.data && !query.data.available && (
        <p className="muted">Запись недоступна: нужны база и календарь.</p>
      )}
      {query.data?.available && offers.length === 0 && (
        <p className="muted">
          Наборов пока нет. Попросите агента: «сделай слоты на вторник с 10 до 13 по 30 минут для
          Ивана и Петра».
        </p>
      )}
      <div className="bookings">
        {offers.map((offer) => (
          <OfferCard key={offer.id} offer={offer} canWrite={canWrite} />
        ))}
      </div>
    </div>
  );
}
